import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const AXIS_LABEL_STYLE = { color: "#AAAAAA", font: { size: 14, weight: 500 } };

export const defaultOptions = {
  borderWidth: 0,
  maintainAspectRatio: false,
  animations: { duration: 1 },

  onHover,
  onClick,
};

export const defaultPlugins = {
  legend: { display: false },
  tooltip: {
    enabled: false,

    mode: 'y',
    axis: 'y',

    position: "nearest",
    intersect: true,

    external: externalTooltipHandler,
  },
};

export const donutPlugins = {
  tooltip: {
    enabled: false,
    mode: "dataset",
    position: "average",
    external: externalTooltipHandler,
  },
};

function getOrCreateTooltip(chart) {
  let tooltipEl = chart.canvas.parentNode.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.classList.add("tooltip-container");

    const container = document.createElement("article");
    container.style.margin = "0px";

    tooltipEl.appendChild(container);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

function externalTooltipHandler(context) {
  // Tooltip Element
  const { chart, tooltip } = context;

  const tooltipEl = getOrCreateTooltip(chart);
  const chartTitle = chart.config._config.options.plugins.title || "";

  const formatter = chart.config._config.options.plugins.tooltip.formatter;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    let title = ""
    if (tooltip.title && tooltip.title.length) title = tooltip.title[0];

    let tooltipTitle = tooltip.title;
    const bodyLines = tooltip.body.map(b => b.lines);

    const header = document.createElement("header");

    const titleEl = document.createElement("span");
    const valueEl = document.createElement("span");

    titleEl.appendChild(document.createTextNode(chartTitle));
    if (title && bodyLines.length > 1) {
      titleEl.classList.add("tooltip-key");
      valueEl.appendChild(document.createTextNode(title));
    }

    header.appendChild(titleEl);
    header.appendChild(valueEl);

    let total = 0;
    if (tooltip.total) {
      total = bodyLines.reduce((prev, current) => {
        let split = current[0].split(":");
        let value = Number(split[split.length - 1]);
        return (prev + Number(value));
      }, 0);
      if (!Number.isInteger(total)) total = total.toFixed(2);
    }

    const main = document.createElement("main");
    bodyLines.forEach((body, i) => {
      let split = body[0].split(":");
      if (split.length === 2) [tooltipTitle, body] = split;

      if(!Number(body)) return;

      if (formatter && body) body = body.map(formatter);

      const rowEl = document.createElement("div");
      rowEl.classList.add("tooltip-row");

      const colors = tooltip.labelColors[i];

      const colorEl = document.createElement("span");
      colorEl.classList.add("tooltip-color");
      colorEl.style.background = switchOpacity(colors.backgroundColor, 1);

      const labelEl = document.createElement("span");
      const label = document.createTextNode(tooltipTitle);
      labelEl.appendChild(label);
      labelEl.classList.add("tooltip-label");

      const valueContainer = document.createElement("span");

      if (tooltip.total && bodyLines.length > 1) {
        const percEl = document.createElement("span");
        percEl.classList.add("tooltip-percent");
        const percent = ((Number(body) / total) * 100).toFixed(0) + "%";
        if (Number(body)) {
          percEl.appendChild(document.createTextNode(percent));
          valueContainer.appendChild(percEl);
        }
      }

      const valueEl = document.createElement("span");
      valueEl.classList.add("tooltip-percent-value");

      const value = document.createTextNode(body);
      valueEl.appendChild(value);
      valueContainer.appendChild(valueEl);

      rowEl.appendChild(colorEl);
      rowEl.appendChild(labelEl);
      rowEl.appendChild(valueContainer);
      main.appendChild(rowEl);
    });

    const container = tooltipEl.querySelector("article");

    // Remove old children
    while (container.firstChild) {
      container.firstChild.remove();
    }

    if (tooltip.total && bodyLines.length > 1) {
      // Adding Total Row
      const rowEl = document.createElement("div");
      rowEl.classList.add("tooltip-row");

      const sumLabelEl = document.createElement("span");
      sumLabelEl.appendChild(document.createTextNode("Total"));
      sumLabelEl.classList.add("tooltip-label", "tooltip-key");

      const valLabelEl = document.createElement("span");
      const value = document.createTextNode(total);
      valLabelEl.appendChild(value);

      rowEl.appendChild(sumLabelEl);
      rowEl.appendChild(valLabelEl);
      main.appendChild(rowEl);
    }

    // Add new children
    container.appendChild(header);
    container.appendChild(main);
  }

  let { offsetTop: positionY, offsetLeft: positionX, clientWidth: chartWidth } = chart.canvas;

  const isLeftSide = tooltip.caretX < chartWidth / 2;
  let tooltipX;
  if (isLeftSide) tooltipX = positionX + tooltip.caretX + 10;
  else tooltipX = positionX + tooltip.caretX - tooltipEl.clientWidth - 10;

  let tooltipY = positionY + tooltip.caretY - tooltipEl.clientHeight / 2;

  const isHorizontal = chart.config.options.indexAxis === 'y';
  if (isHorizontal) {
      tooltipY = positionY + tooltip.caretY + 10;
      if (isLeftSide) tooltipX = positionX + tooltip.caretX - tooltipEl.clientWidth - 10;
      else tooltipX = positionX + tooltip.caretX + 10;
  }

  if (tooltipX < positionX) tooltipX = positionX + 10;
  if (tooltipX + tooltipEl.clientWidth > positionX + chartWidth) {
      tooltipX = positionX + chartWidth - tooltipEl.clientWidth - 10;
  }

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${tooltipX}px`;
  tooltipEl.style.top = `${tooltipY}px`;
  tooltipEl.style.transform = "translateX(0)";
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

const switchOpacity = (color, opacity) => {
  let split = color.split(",");
  split[split.length - 1] = opacity + ")";
  return split.join(",");
};

const toggleOpacity = (dataset, opacity) => {
  if (Array.isArray(dataset.backgroundColor)) {
    dataset.backgroundColor = dataset.backgroundColor.map((color) => switchOpacity(color, opacity));
  } else {
    let color = dataset.backgroundColor;
    dataset.backgroundColor = new Array(dataset.data.length).fill(switchOpacity(color, opacity));
  }
};

function onHover(e, activeElements, chart) {
  const currActive = activeElements.length ? activeElements[0] : null;
  if (chart.tooltip && chart.tooltip.opacity && currActive) {
    chart.tooltip.dataPoints[currActive.datasetIndex].dataset.active = true;
  }
  const prevActive = chart.prevActive || { datasetIndex: -1, index: -1 };

  const redraw = () => {
    chart.update();
  };

  if (chart.onClick) {
    const hoverElements = chart.getElementsAtEventForMode(e, "dataset", { intersect: true });
    if (hoverElements.length) {
      e.native.target.style.cursor = "pointer";
    } else {
      e.native.target.style.cursor = "default";
    }
  }

  if (!currActive) {
    let mode = chart.config._config.options.indexAxis || "index";

    const elements = chart.getElementsAtEventForMode(e, mode, { intersect: !chart.tooltip.row }, true);
    if (elements.length) {
      // const activeIndex = elements[0].index;

      // if (chart.prevActiveIndex !== activeIndex) {
      //   chart.data.datasets.forEach((ds) => {
      //     toggleOpacity(ds, 0.3);
      //     ds.backgroundColor[activeIndex] = switchOpacity(ds.backgroundColor[activeIndex], 1);
      //   });

      //   redraw();
      // }
      // chart.prevActiveIndex = activeIndex;
    } else {
      chart.data.datasets.forEach((dataset) => {
        toggleOpacity(dataset, 1);
      });
      redraw();

      chart.prevActiveIndex = null;
    }
  } else {
    if (currActive.datasetIndex !== prevActive.datasetIndex || currActive.index !== prevActive.index) {
      chart.data.datasets.forEach((ds, i) => {
        toggleOpacity(ds, 0.3);

        if (i === currActive.datasetIndex) {
          ds.backgroundColor[currActive.index] = switchOpacity(ds.backgroundColor[currActive.index], 1);
        }
      });

      redraw();
    }
  }

  chart.prevActive = currActive;
}

function onClick(event, active, chart) {
  if (!chart.onClick) return;

  if (active.length) {
    const { index, datasetIndex } = active[0];
    return chart.onClick(index, datasetIndex);
  }

  const elementsAtClick = chart.getElementsAtEventForMode(event, "y", { intersect: false }, true);
  if (elementsAtClick.length) {
    const { index } = elementsAtClick[0];
    return chart.onClick(index);
  }
}

export const defaultRootPlugins = [
  {
    id: "mouseout",
    beforeEvent(chart, args) {
      const event = args.event;

      if (event.type === "mouseout") {
        chart.data.datasets.forEach((dataset) => {
          toggleOpacity(dataset, 1);
        });

        chart.update();
      }
    }
  },
  {
    id: 'emptyDoughnut',
    afterDraw(chart, args, options) {
      const {datasets} = chart.data;
      const {color, width, radiusDecrease} = options;
      let hasData = false;

      for (let i = 0; i < datasets.length; i += 1) {
        const dataset = datasets[i];
        hasData |= dataset.data.length > 0;
      }

      if (!hasData) {
        const {chartArea: {left, top, right, bottom}, ctx} = chart;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        const r = Math.min(right - left, bottom - top) / 2;

        ctx.beginPath();
        ctx.lineWidth = width || 2;
        ctx.strokeStyle = color || 'rgba(255, 128, 0, 0.5)';
        ctx.arc(centerX, centerY, (r - radiusDecrease || 0), 0, 2 * Math.PI);
        ctx.stroke();
      }
    }
  }
];
